import React from 'react';
import { Box, SimpleGrid, Stack, Text, Tooltip, UnstyledButton } from '@mantine/core';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import type { CalendarItem } from '../../hooks/useCalendarApi';
import { layoutMonth, zonedDay } from './layoutMonth';
import { CalendarItemRow, itemTitle } from './CalendarItemRow';
import { sourceDot, sourceSoftBg, sourceSoftText } from './sourceColor';
import './calendar.css';

/** Высота строки периода плюс зазор: клетка отступает на неё сверху. */
const PERIOD_BAND = 22;

export const MonthGrid: React.FC<{
  year: number;
  month: number;
  items: CalendarItem[];
  timeZone: string;
  showPerson: boolean;
  onMore: (date: string) => void;
}> = ({ year, month, items, timeZone, showPerson, onMore }) => {
  const { t } = useTranslation();
  const { weeks, periods } = layoutMonth(year, month, items, timeZone);

  // День считаем в поясе организации, а не браузера: иначе у человека во
  // Владивостоке «сегодня» подсвечивается на сутки мимо.
  const today = zonedDay(new Date().toISOString(), timeZone);

  // Период занимает несколько недель: название пишем только в первой, дальше
  // полоса идёт молча. Иначе один и тот же заголовок повторяется весь месяц.
  const firstWeekOf = new Map<string, number>();
  for (const period of periods) {
    const seen = firstWeekOf.get(period.item.id);
    if (seen === undefined || period.week < seen) firstWeekOf.set(period.item.id, period.week);
  }

  return (
    <Stack gap={0}>
      <SimpleGrid cols={7} spacing={0}>
        {[0, 1, 2, 3, 4, 5, 6].map((i) => (
          <Text key={i} size="xs" c="dimmed" ta="center" pb={6} tt="lowercase">
            {dayjs()
              .day((i + 1) % 7)
              .format('dd')}
          </Text>
        ))}
      </SimpleGrid>

      <div className="calendar-grid">
        {weeks.map((week, w) => {
          const weekPeriods = periods.filter((p) => p.week === w);
          const lanes = Math.max(0, ...weekPeriods.map((p) => p.lane + 1));
          return (
            <Box key={week[0].date} pos="relative">
              <SimpleGrid cols={7} spacing={0}>
                {week.map((cell, columnIndex) => {
                  const isToday = cell.date === today;
                  const isWeekend = columnIndex >= 5;
                  return (
                    <div
                      key={cell.date}
                      className={[
                        'calendar-cell',
                        isWeekend ? 'calendar-cell--weekend' : '',
                        cell.isInMonth ? '' : 'calendar-cell--outside',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      <Text
                        size="xs"
                        className={isToday ? 'calendar-cell__today' : undefined}
                        c={cell.isInMonth ? undefined : 'dimmed'}
                        style={{ fontVariantNumeric: 'tabular-nums' }}
                      >
                        {Number(cell.date.slice(8))}
                      </Text>
                      <Stack gap={2} mt={lanes ? lanes * PERIOD_BAND : 4}>
                        {cell.items.map((item) => (
                          <CalendarItemRow key={item.id} item={item} showPerson={showPerson} />
                        ))}
                        {cell.more > 0 && (
                          <UnstyledButton
                            className="calendar-more"
                            onClick={() => onMore(cell.date)}
                          >
                            <Text size="xs" c="dimmed">
                              {t('calendar.page.more', { count: cell.more })}
                            </Text>
                          </UnstyledButton>
                        )}
                      </Stack>
                    </div>
                  );
                })}
              </SimpleGrid>

              {weekPeriods.map((period) => {
                const isFirstWeek = firstWeekOf.get(period.item.id) === period.week;
                const title = itemTitle(t, period.item);
                return (
                  <Box
                    key={period.item.id}
                    pos="absolute"
                    top={20 + period.lane * PERIOD_BAND}
                    left={`${(period.startCol / 7) * 100}%`}
                    w={`${(period.span / 7) * 100}%`}
                    px={3}
                  >
                    <Tooltip label={title} withArrow openDelay={400}>
                      <div
                        className="calendar-period"
                        style={{
                          background: sourceSoftBg(period.item.source),
                          borderLeft: `3px solid ${sourceDot(period.item.source)}`,
                        }}
                      >
                        <Text size="xs" truncate c={sourceSoftText(period.item.source)}>
                          {isFirstWeek ? title : ' '}
                        </Text>
                      </div>
                    </Tooltip>
                  </Box>
                );
              })}
            </Box>
          );
        })}
      </div>
    </Stack>
  );
};
